import React, { useCallback, useMemo, useRef } from 'react'
import { useService } from '@pushly/aqe/lib/hooks'
import { useUserPreferences } from '@pushly/aqe/lib/providers/user-preferences-provider/core/index'
import { useRecents } from '../../../components/sidebar/active-domain-selector/hooks/use-recents'
import { PlatformUserDomainRecord } from '../../../models/domain/platform-user-domain-record'
import { AppService } from '../../../services/app'
import { accessSwitchFailureMessage, openAccessPath } from '../../../core/access/open-access-path'
import { simpleNotification } from '../../../_utils/utils'
import { AppState } from '../../../stores/app'

/** The active-domain selector's own default for how many recently opened domains it remembers. */
const RECENT_DOMAINS_LIMIT = 3

/**
 * Opening something that lives inside a domain from the organization-context dashboard. Only a domain the user
 * can switch to can be opened, because the active-domain context cannot be set for any other; callers show a
 * link for those and plain text for the rest. Opening resolves the domain through the access owner as the active-domain
 * selector does, routes to `path` only when it resolved, and only then remembers the domain among the recently opened ones. A modified click opens the
 * path in a new window without switching.
 */
export const useOpenInDomainContext = () => {
    const appState = useService(AppState)
    const appService = useService(AppService)
    // Recently opened domains are stored at the user level, not per organization, as the selector does.
    const userLevelPreferences = useUserPreferences(appState.currentUser?.id)
    // Keyed on the domain ids, which are read on every render so an observing component re-renders when the list
    // changes, including when it is changed in place.
    const switchableDomains = appState.currentUserDomains ?? []
    const switchableDomainIdsKey = switchableDomains.map((record) => record.id).join(',')
    const switchableDomainsById = useMemo(
        () => new Map<number, PlatformUserDomainRecord>(switchableDomains.map((record) => [record.id, record])),
        [switchableDomainIdsKey],
    )
    const { addToRecents } = useRecents(userLevelPreferences, switchableDomainsById, RECENT_DOMAINS_LIMIT)

    const canOpenDomain = useCallback(
        (domainId: number) => switchableDomainsById.has(domainId),
        [switchableDomainsById],
    )

    // What the click handler needs changes on every render (the recents list is re-read each time), but the handler
    // itself must keep one identity so tables that memoize their columns on it are not rebuilt on every render.
    const latest = useRef({ switchableDomainsById, addToRecents })
    latest.current = { switchableDomainsById, addToRecents }

    // The dashboard is an organization-context page, so opening a domain is always a switch of context.
    const openInDomain = useCallback(
        async (domainId: number, path: string, event: React.MouseEvent<HTMLAnchorElement>) => {
            event.preventDefault()
            if (event.metaKey || event.ctrlKey) {
                window.open(`${appState.platformHost}${path}`)
                return
            }
            const record = latest.current.switchableDomainsById.get(domainId)
            if (!record) return
            // The same intent as the selectors, reached through the service so a page that renders outside the
            // router or the owner provider still works. The service decides between a router push and a full
            // load when a new version is out. A refused domain shows the selector's message and the page stays.
            const result = await openAccessPath(
                appState.accessOwner,
                { pathname: () => appService.currentRoute, push: (target) => appService.route(target) },
                path,
            )
            if (result.ok) {
                latest.current.addToRecents(domainId)
            } else if (!result.superseded) {
                simpleNotification('error', accessSwitchFailureMessage(result.kind))
            }
        },
        [appState, appService],
    )

    return { canOpenDomain, openInDomain }
}
